/**
 * Top-level application component.
 *
 * Responsibilities:
 * - Bootstrap the authenticated user state on initial mount (`async_loaduser`).
 * - Initialize marketing/analytics integrations (Facebook Pixel) only after consent is granted.
 * - Provide global app-wide providers and side-effect components:
 *   - `NovuProvider` for notifications (inbox config + realtime toasts)
 *   - `ToastContainer` for `react-toastify` toasts
 *   - `AiChatWidget` for AI chat access
 * - Define all React Router routes with route-level code splitting (`React.lazy` + `Suspense`).
 */
import React, { useCallback, useEffect, Suspense } from "react";
import "./App.css";
import Layout from "./components/Layout";
import { useDispatch, useSelector } from "react-redux";
import { async_loaduser } from "./store/actions/studentsActions";
import { Route, Routes, Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";
import NovuProvider from "./components/notifications-component/NovuProvider";
import ProtectedRoute from "./helpers/ProtectedRoute";
import CounsellorProtectedRoute from "./helpers/CounsellorProtectedRoute";
import NonChildOnly from "./helpers/NonChildOnly";
import { ThemeProvider } from "./components/student-dashboard-components/theme";
import { ThemeProvider as TeacherThemeProvider } from "./components/teacher-dashboard-components/theme";
import AppToastProvider from "./components/notifications-component/AppToastProvider";
import "react-toastify/dist/ReactToastify.css";
import facebookPixel from "./services/facebookPixel";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { initRemarketing } from "./utils/remarketing";
import { persistAttributionFromLocation } from "./components/marketing/utils/utm";
import useLoadingTimeout from "./hooks/useLoadingTimeout";
// BUG-004/005: getStoredToken no longer needed - auth is via httpOnly cookie

/**
 * Suspense fallback shown while route-level code-split bundles are loading.
 * @returns {JSX.Element}
 */
const RouteFallback = () => {
  const { t } = useTranslation('global');
  const { hasTimedOut, resetTimeout } = useLoadingTimeout(true, 12000);

  const handleReload = useCallback(() => {
    resetTimeout();
    window.location.reload();
  }, [resetTimeout]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
      backgroundColor: '#F8FAFC',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '1rem'
    }}>
      {!hasTimedOut ? (
        <>
          <div style={{
            width: '36px',
            height: '36px',
            border: '4px solid #E2E8F0',
            borderTop: '4px solid #113F67',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            marginTop: '12px',
            color: '#475569',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            {t('common.Loading')}
          </p>
        </>
      ) : (
        <div style={{
          width: '100%',
          maxWidth: '360px',
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)'
        }}>
          <p style={{
            margin: '0 0 10px 0',
            color: '#334155',
            fontSize: '14px',
            fontWeight: 600
          }}>
            {t('errors.pageLoadingTimeout')}
          </p>
          <p style={{
            margin: '0 0 14px 0',
            color: '#64748b',
            fontSize: '13px'
          }}>
            {t('errors.retryMobileRouteLoad')}
          </p>
          <button
            type="button"
            onClick={handleReload}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '14px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: '#113F67',
              cursor: 'pointer'
            }}
          >
            {t('actions.reloadPage')}
          </button>
        </div>
      )}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// The old schedule URL is still present in campaign links, but its component
// depended on the retired free-package endpoint. Keep the URL compatible and
// move visitors into the current trial-booking flow instead of rendering a
// half-working legacy form.
const LegacyScheduleRedirect = () => {
  const { TeacherID } = useParams();
  const query = TeacherID ? `?teacher=${encodeURIComponent(TeacherID)}` : '';
  return <Navigate replace to={`/lekcja-probna${query}`} />;
};

/**
 * Functional error fallback UI used by ErrorBoundary.
 * Extracted so it can use the useTranslation hook.
 */
const ErrorFallbackUI = () => {
  const { t } = useTranslation('global');
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
      <h2>{t('errors.somethingWentWrong')}</h2>
      <p>{t('errors.failedToLoadPage')}</p>
      <button onClick={() => window.location.reload()}
        style={{ padding: '10px 24px', cursor: 'pointer', background: '#4A90D9', color: '#fff', border: 'none', borderRadius: '6px' }}>
        {t('actions.reload')}
      </button>
    </div>
  );
};

/**
 * ErrorBoundary catches errors from lazy-loaded chunks (e.g. network failures,
 * stale deployments) and shows a recovery UI instead of a blank page.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return <ErrorFallbackUI />;
    }
    return this.props.children;
  }
}

/**
 * 404 page component with i18n support.
 */
const NotFoundPage = () => {
  const { t } = useTranslation('global');
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px' }}>
      <h1 style={{ fontSize: '72px', margin: '0', color: '#666' }}>404</h1>
      <p style={{ fontSize: '20px', color: '#666' }}>{t('errors.pageNotFound')}</p>
      <a href="/" style={{ color: '#113F67', textDecoration: 'none' }}>{t('actions.goToHomepage')}</a>
    </div>
  );
};

// Route-level code splitting to keep the landing page bundle small.
const TeacherDetails = React.lazy(() => import("./components/TeacherDetails"));
const CourseDetails = React.lazy(() => import("./components/CourseDetails"));

// Student routes
const Profile = React.lazy(() => import("./components/student-dashboard-components/profile/Profile"));
const StudentDashboard = React.lazy(() => import("./components/StudentDashboard"));
const StudentDashMobileComplete = React.lazy(() => import("./components/student-dashboard-components/dashboard/StudentDashMobileComplete"));
const Bookings = React.lazy(() => import("./components/student-dashboard-components/booking/Bookings"));
const Enquirys = React.lazy(() => import("./components/student-dashboard-components/utils/Enquirys"));
  const ReferralDashboard = React.lazy(() => import("./components/student-dashboard-components/dashboard/ReferralDashboard"));
  const ChildZonePage = React.lazy(() => import("./components/student-dashboard-components/child-zone/ChildZonePage"));
const StudentEvaluationsPage = React.lazy(() => import("./components/student-dashboard-components/evaluation/StudentEvaluationPage"));
const StudentTeacherDirectory = React.lazy(() => import("./components/student-dashboard-components/teachers/StudentTeacherDirectory"));
const StudentMaterials = React.lazy(() => import("./components/student-dashboard-components/materials/StudentMaterial"));
const PracticePage = React.lazy(() => import("./components/student-dashboard-components/practice/PracticePage"));
const PracticeEngineDevPage = React.lazy(() => import("./components/student-dashboard-components/practice/engine/PracticeEngineDevPage"));
const AddStudentEnquiry = React.lazy(() => import("./components/student-dashboard-components/utils/AddStudentEnquiry"));
const AfterLessonPage = React.lazy(() => import("./components/student-dashboard-components/after-lesson/AfterLessonPage"));
const WalletPage = React.lazy(() => import("./components/student-dashboard-components/billing-v2/WalletPage"));
const BuyLessonsPage = React.lazy(() => import("./components/student-dashboard-components/billing-v2/BuyLessonsPage"));
const BookLessonsPage = React.lazy(() => import("./components/student-dashboard-components/billing-v2/BookLessonsPage"));
const SubscriptionSchedulePage = React.lazy(() => import("./components/student-dashboard-components/billing-v2/SubscriptionSchedulePage"));
const SubscriptionPaymentPage = React.lazy(() => import("./components/student-dashboard-components/billing-v2/SubscriptionPaymentPage"));

const PaymentStatus = React.lazy(() => import("./components/student-dashboard-components/payment/PaymentStatus"));
const StudentPaymentHistoryPage = React.lazy(() => import("./components/student-dashboard-components/payment/StudentPaymentHistoryPage"));
const EditBooking = React.lazy(() => import("./components/student-dashboard-components/booking/EditBooking"));
const StudentInvoice = React.lazy(() => import("./components/student-dashboard-components/utils/StudentInvoice"));

// Admin routes
const AdminOperationsDashboardPage = React.lazy(() => import("./components/admin-dashboard-components/AdminOperationsDashboardPage"));
const AdminDashboard = React.lazy(() => import("./components/admin-dashboard-components/AdminDashboard"));
const AdminTeachers = React.lazy(() => import("./components/admin-dashboard-components/AdminTeachers"));
const AdminStudents = React.lazy(() => import("./components/admin-dashboard-components/AdminStudents"));
const AdminPeoplePage = React.lazy(() => import("./components/admin-dashboard-components/AdminPeoplePage"));
const AdminPracticeMaterials = React.lazy(() => import("./components/admin-dashboard-components/AdminPracticeMaterials"));
const AdminBookings = React.lazy(() => import("./components/admin-dashboard-components/AdminBookings"));
const AdminPayments = React.lazy(() => import("./components/admin-dashboard-components/AdminPayments"));
const AdminEnquirys = React.lazy(() => import("./components/admin-dashboard-components/AdminEnquirys"));
const AdminSettings = React.lazy(() => import("./components/admin-dashboard-components/AdminSettings"));
const AdminAddTeachers = React.lazy(() => import("./components/admin-dashboard-components/AdminAddTeachers"));
const AdminAddStudents = React.lazy(() => import("./components/admin-dashboard-components/AdminAddStudents"));
const AdminTeachersDetails = React.lazy(() => import("./components/admin-dashboard-components/AdminEditPages/AdminTeachersDetails"));
const AdminEditTeacher = React.lazy(() => import("./components/admin-dashboard-components/AdminEditPages/AdminEditTeacher"));
const AdminStudentsDetails = React.lazy(() => import("./components/admin-dashboard-components/AdminEditPages/AdminStudentsDetails"));
const AdminEditStudent = React.lazy(() => import("./components/admin-dashboard-components/AdminEditPages/AdminEditStudent"));
const BugList = React.lazy(() => import("./components/admin-dashboard-components/bugList"));
const ExportComponent = React.lazy(() => import("./components/admin-dashboard-components/ExportComponent"));
const AdminEvaluationDashboard = React.lazy(() => import("./components/admin-dashboard-components/AdminEvaluation"));
const AdminReferralDashboard = React.lazy(() => import("./components/admin-dashboard-components/referalDashboard"));
const AdminOnlineUsersPage = React.lazy(() => import("./components/admin-dashboard-components/OnlineUsersAdmin"));
const AdminCounsellors = React.lazy(() => import("./components/admin-dashboard-components/AdminCounsellors"));
const AdminAddBooking = React.lazy(() => import("./components/admin-dashboard-components/AdminAddBooking"));
const AdminEditBooking = React.lazy(() => import("./components/admin-dashboard-components/AdminEditPages/AdminEditBooking"));
const AdminAddAvailability = React.lazy(() => import("./components/admin-dashboard-components/AdminAddAvailability"));
const AdminAddAvailabilityTime = React.lazy(() => import("./components/admin-dashboard-components/AdminAddAvailabilityTime"));
const AdminEditTeacherEditAvailability = React.lazy(() => import("./components/admin-dashboard-components/AdminEditPages/AdminEditTeacherEditAvailability"));
const TeacherInvitesPage = React.lazy(() => import("./components/admin-dashboard-components/TeacherInvitesPage"));
const Invoice = React.lazy(() => import("./components/admin-dashboard-components/invoice"));
const AdminFinance = React.lazy(() => import("./components/admin-dashboard-components/AdminFinance"));
const AdminLiveSessions = React.lazy(() => import("./components/admin-dashboard-components/AdminLiveSessions"));
const AdminSessionHistory = React.lazy(() => import("./components/admin-dashboard-components/AdminSessionHistory"));
const AdminAiChatLogs = React.lazy(() => import("./components/admin-dashboard-components/AdminAiChatLogs"));
const AdminActivityLog = React.lazy(() => import("./components/admin-dashboard-components/AdminActivityLog"));
const AdminLeadCenter = React.lazy(() => import("./components/admin-dashboard-components/AdminLeadCenter"));
const AdminVoiceLeads = React.lazy(() => import("./components/admin-dashboard-components/AdminVoiceLeads"));
const AdminVoiceLeadDetail = React.lazy(() => import("./components/admin-dashboard-components/AdminVoiceLeadDetail"));
const StudentHistoryPage = React.lazy(() => import("./components/student-dashboard-components/history/StudentHistoryPage"));
const TeacherHistoryPage = React.lazy(() => import("./components/teacher-dashboard-components/TeacherHistoryPage"));
const AdminWallets = React.lazy(() => import("./components/admin-dashboard-components/AdminWallets"));
const AdminTeacherNotes = React.lazy(() => import("./components/admin-dashboard-components/AdminTeacherNotes"));
const AdminTeacherRatings = React.lazy(() => import("./components/admin-dashboard-components/AdminTeacherRatings"));
const AdminSubscriptions = React.lazy(() => import("./components/admin-dashboard-components/AdminSubscriptions"));

// Teacher routes
const TeacherDashboard = React.lazy(() => import("./components/teacher-dashboard-components/TeacherDashboardSidebar"));
const TeacherDash = React.lazy(() => import("./components/teacher-dashboard-components/TeacherDash"));
const TeacherProfile = React.lazy(() => import("./components/teacher-dashboard-components/TeacherProfile"));
const TeacherBookings = React.lazy(() => import("./components/teacher-dashboard-components/TeacherBookings"));
const TeacherWallet = React.lazy(() => import("./components/teacher-dashboard-components/TeacherWallet"));
const TeacherLessonCenter = React.lazy(() => import("./components/teacher-dashboard-components/TeacherLessonCenter"));
const TeacherEvaluationsPage = React.lazy(() => import("./components/teacher-dashboard-components/TeacherEvaluationsPage"));
const TeacherStudentRatings = React.lazy(() => import("./components/teacher-dashboard-components/TeacherStudentRatings"));
const TeacherMaterialsPage = React.lazy(() => import("./components/teacher-dashboard-components/TeacherMaterialsPage"));
const TeacherNotesPage = React.lazy(() => import("./components/teacher-dashboard-components/TeacherNotesPage"));
const TeacherEditAvailability = React.lazy(() => import("./components/teacher-dashboard-components/TeacherEditAvailability"));
const TeacherEditAvailabilityBeta = React.lazy(() => import("./components/teacher-dashboard-components/TeacherEditAvailabilityBeta"));

// Accountant routes
const AccontantDashboard = React.lazy(() => import("./components/acoountant-dashboard-components/AccontantDashboard"));
const AccontantDash = React.lazy(() => import("./components/acoountant-dashboard-components/AccontantDash"));
const AccontantPayment = React.lazy(() => import("./components/acoountant-dashboard-components/AccontantPayment"));
const AccountantInvoice = React.lazy(() => import("./components/acoountant-dashboard-components/AccountantInvoice"));

// Counsellor routes
const CounsellorLogin = React.lazy(() => import("./components/CounsellorLogin"));
const CounsellorDashboard = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorDashboard"));
const CounsellorDash = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorDash"));
const CounsellorInvitesPage = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorInvitesPage"));
const CounsellorTrialBookingsPage = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorTrialBookingsPage"));
const CounsellorTeachersPage = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorTeachersPage"));
const CounsellorVoiceLeadsPage = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorVoiceLeadsPage"));
const CounsellorVoiceLeadDetail = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorVoiceLeadDetail"));
const CounsellorDecisionQueuePage = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorDecisionQueuePage"));
const CounsellorProfilePage = React.lazy(() => import("./components/counsellor-dashboard-components/CounsellorProfilePage"));

// Misc routes
const PaymentRedirect = React.lazy(() => import("./PaymentRedirect"));
const DirectLinkPage = React.lazy(() => import("./components/meeting-components/DirectLinkPage"));
const AccountDeletion = React.lazy(() => import("./components/AccountDeletion"));
const MobileSignIn = React.lazy(() => import("./components/MobileSignIn"));
const LandingPage = React.lazy(() => import("./components/landingcomponents/PenpotLandingPage"));
const TeacherSelection = React.lazy(() => import("./components/landingcomponents/TeacherSelectionModal"));
const AboutUs = React.lazy(() => import("./components/landingcomponents/draftPages/aboutUs"));

// Marketing / SEO pages (content hub + lead capture + diagnostic flow)
const GuidesIndexPage = React.lazy(() => import("./components/marketing/pages/GuidesIndexPage"));
const GuideArticlePage = React.lazy(() => import("./components/marketing/pages/GuideArticlePage"));
const Plan14DaysPage = React.lazy(() => import("./components/marketing/pages/Plan14DaysPage"));
const PlanThankYouPage = React.lazy(() => import("./components/marketing/pages/PlanThankYouPage"));
const DiagnosticQuizPage = React.lazy(() => import("./components/marketing/pages/DiagnosticQuizPage"));
const DiagnosticResultPage = React.lazy(() => import("./components/marketing/pages/DiagnosticResultPage"));
const TrialLessonPage = React.lazy(() => import("./components/marketing/pages/TrialLessonPage"));
const TrialResultPage = React.lazy(() => import("./components/marketing/pages/TrialResultPage"));
const BookTrialLesson = React.lazy(() => import("./components/landingcomponents/AddLandingPage"));
const InviteSignupPage = React.lazy(() => import("./components/InviteSignupPage"));
const PrivacyPolicy = React.lazy(() => import("./components/PrivacyPolicy"));
const NewLoginPage = React.lazy(() => import("./components/NewLoginPage"));
const NewSignupPage = React.lazy(() => import("./components/NewSignupPage"));
const NewForgotPasswordPage = React.lazy(() => import("./components/NewForgotPasswordPage"));
const SetupPasswordPage = React.lazy(() => import("./components/SetupPasswordPage"));

// Meetings + chat
const AiChatWidget = React.lazy(() => import("./components/ai-chat/AiChatWidget"));
const NotificationsPage = React.lazy(() => import("./components/notifications-component/NotificationsPage"));
const AdminNotificationsPage = React.lazy(() => import("./components/admin-dashboard-components/AdminNotificationsPage"));

const DASHBOARD_STYLE_ROUTE_REGEX =
  /^\/(Student-dashboard|Teacher-dashboard|Admin-Dashboard|Counsellor-dashboard|Acoountant-dashboard|Accountant-Dashboard)(\/|$)/i;
const CHECKOUT_AI_WIDGET_HIDDEN_ROUTE_REGEX =
  /^\/student-dashboard\/(?:checkdetails|paymentspage|paymentpage|practice|subscriptions\/[^/]+\/(?:schedule|checkout))(?:\/|$)/i;
const STUDENT_DASHBOARD_HOME_REGEX =
  /^\/student-dashboard\/dash(?:\/|$)/i;
const CANONICAL_LANDING_ROUTE_REGEX =
  /^\/(?:$|landing-page|landing)(?:\/|$)/i;
const STUDENT_HELP_CHAT_ROUTE_REGEX =
  /^\/student-dashboard\/enquirys(?:\/|$)/i;
const AUTH_BOOTSTRAP_ROUTE_REGEX =
  /^\/(?:mobile-signin|Student-dashboard|Teacher-dashboard|Admin-Dashboard|Accountant-Dashboard|Counsellor-Dashboard|payment-redirect|trial-result|after-lesson|setup-password)(?:\/|$)/i;
const LEGACY_HASH_ROUTE_REGEX = /^#\//;

/**
 * Main app component that renders global providers and configures routes.
 * @returns {JSX.Element}
 */
const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  // Used by a few pages that need to know "who is currently logged in" (e.g. admin online users panel).
  const currentUser = useSelector(
    (state) => state.auth?.user || state.user || state.students?.user
  );
  const isDashboardLikeRoute = DASHBOARD_STYLE_ROUTE_REGEX.test(location.pathname || "");
  const isStudentHelpChatRoute = STUDENT_HELP_CHAT_ROUTE_REGEX.test(location.pathname || "");
  const shouldBootstrapAuth = AUTH_BOOTSTRAP_ROUTE_REGEX.test(location.pathname || "");
  const hasLegacyHashRoute = LEGACY_HASH_ROUTE_REGEX.test(location.hash || "");
  const hideAiChatWidget =
    // The authenticated dashboard designs own their support entry points. Keep
    // the global marketing launcher out of those screens so it cannot cover
    // role-owned cards, tables, or mobile bottom navigation.
    (!isStudentHelpChatRoute && isDashboardLikeRoute) ||
    CHECKOUT_AI_WIDGET_HIDDEN_ROUTE_REGEX.test(location.pathname || "") ||
    STUDENT_DASHBOARD_HOME_REGEX.test(location.pathname || "") ||
    CANONICAL_LANDING_ROUTE_REGEX.test(location.pathname || "");

  // Capture gclid/fbclid/UTM params globally on every route change.
  // Previously this only ran inside MarketingShell, so visitors landing
  // on non-marketing routes (e.g. /) lost their click IDs.
  useEffect(() => {
    persistAttributionFromLocation(location.search, location.pathname);
  }, [location.search, location.pathname]);

  /**
   * Lazily loads dashboard-only global styles only when user enters dashboard routes.
   * This reduces initial CSS/JS parsing on marketing pages.
   */
  useEffect(() => {
    if (!isDashboardLikeRoute || window.__speakableDashboardGlobalStylesLoaded) {
      return;
    }

    window.__speakableDashboardGlobalStylesLoaded = true;
    void import("./StudentDashboard.css");
    void import("./components/student-dashboard-components/theme/theme.scss");
  }, [isDashboardLikeRoute]);

  /**
   * Bootstrap auth only on authenticated/product routes. Public marketing pages
   * must not emit a harmless-but-noisy 401 /api/me in the browser console.
   */
  useEffect(() => {
    if (!shouldBootstrapAuth) return;
    dispatch(async_loaduser());
  }, [dispatch, shouldBootstrapAuth]);


  /**
   * On first mount:
   * - initialize Facebook Pixel after consent is granted (and re-attempt init if consent changes later)
   */
  useEffect(() => {
    const pixelId =
      process.env.REACT_APP_FACEBOOK_PIXEL_ID || facebookPixel.pixelId;

    /**
     * Determine whether the current consent state allows marketing/ads tracking.
     * @param {object} consent
     */
    const hasMarketingConsent = (consent) =>
      consent?.ad_storage === "granted" ||
      consent?.personalization_storage === "granted";

    /**
     * Initialize Facebook Pixel exactly when we have both:
     * - a pixel id (env or fallback)
     * - user consent for marketing storage
     *
     * This keeps us compliant with consent requirements and prevents unnecessary third-party calls.
     * @param {object} consent
     */
    const tryInitFacebookPixel = (consent) => {
      if (!pixelId) {
        console.warn("Facebook Pixel ID is not set; skipping Pixel init");
        return;
      }
      if (!hasMarketingConsent(consent)) {
        return;
      }
      facebookPixel.init(pixelId);

      // Set GA4 user_id for cross-device attribution (if user is logged in)
      try {
        const stored = localStorage.getItem('userData') || sessionStorage.getItem('userData');
        if (stored && typeof window.gtag === 'function') {
          const user = JSON.parse(stored);
          if (user?._id) {
            window.gtag('set', { user_id: user._id });
          }
        }
      } catch (e) { /* ignore */ }
    };

    /**
     * Consent update event handler (custom event emitted by our CMP integration).
     * @param {CustomEvent} event
     */
    const onConsentUpdate = (event) => {
      tryInitFacebookPixel(event?.detail);
    };

    window.addEventListener("cmp:consent", onConsentUpdate);
    // Attempt initialization using the last known consent state (if present).
    tryInitFacebookPixel(window.__cmpConsentState);

    // Initialize remarketing & audience segmentation
    initRemarketing();

    return () => {
      window.removeEventListener("cmp:consent", onConsentUpdate);
    };
  }, []);

  return (
    <HelmetProvider>
    <NovuProvider>
    <AppToastProvider>
      <div>
        {/* ---------------------------------------------------------------------------------------------------- Main Routes  ------------------------- */}
        <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        {hasLegacyHashRoute ? (
          <NotFoundPage />
        ) : (
        <>
        <Routes>
        <Route path="/" element={<Layout />} />
        <Route path="/signup" element={<Layout />} />
        {/* New Login and Signup Pages */}
        <Route path="/new-login" element={<NewLoginPage />} />
        {process.env.NODE_ENV === "development" ? (
          <Route path="/__dev/practice-engine" element={<PracticeEngineDevPage />} />
        ) : null}
        <Route path="/new-signup" element={<NewSignupPage />} />
        <Route path="/forgot-password" element={<NewForgotPasswordPage />} />
        <Route path="/setup-password" element={<SetupPasswordPage />} />
        <Route
          path="/payment-redirect/:redirectToken"
          element={<PaymentRedirect />}
        />
        <Route path="/Landing-Page" element={<LandingPage />} />
        <Route path="/Teacher-Page" element={<TeacherSelection />} />
        <Route path="/about-us" element={<AboutUs />} />

        {/* Marketing / SEO */}
        {/*
         * Keep one canonical landing implementation across the public entry
         * points. The campaign page remains available explicitly for legacy
         * campaign links, but /landing must match the current Penpot design.
         */}
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/landing-campaign" element={<LandingPage />} />
        <Route path="/poradniki" element={<GuidesIndexPage />} />
        <Route path="/poradniki/:slug" element={<GuideArticlePage />} />
        <Route path="/plan-14-dni" element={<Plan14DaysPage />} />
        <Route path="/plan-14-dni/dzieki" element={<PlanThankYouPage />} />
        <Route path="/diagnoza" element={<DiagnosticQuizPage />} />
        <Route path="/diagnoza/wynik" element={<DiagnosticResultPage />} />
        <Route path="/diagnoza/wynik/:attemptId" element={<DiagnosticResultPage />} />
        <Route path="/lekcja-probna" element={<TrialLessonPage />} />
        <Route path="/book-trial-lesson" element={<BookTrialLesson />} />
        <Route path="/trial-result/:token" element={<TrialResultPage />} />
        {/* Keep canonical public destinations stable for old campaigns and bookmarks. */}
        <Route path="/register" element={<Navigate to="/new-signup" replace />} />
        <Route path="/regulamin" element={<Navigate to="/privacy-policy#terms-and-conditions" replace />} />
        <Route path="/terms" element={<Navigate to="/privacy-policy#terms-and-conditions" replace />} />
        {/* Keep the short legacy privacy URL working for old campaigns and bookmarks. */}
        <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/account-deletion" element={<AccountDeletion />} />
        {/* Keep legacy campaign links on the supported booking flow. The old
            Facebook landing expected teacher/availability props that the route
            never supplied and rendered translation placeholders instead. */}
        <Route path="/freelesson" element={<TrialLessonPage />} />
        <Route
          path="/after-lesson/:bookingId"
          element={
            <ProtectedRoute>
              <ThemeProvider>
                <AfterLessonPage />
              </ThemeProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/Student-dashboard/PaymentStatus"
          element={
            <ProtectedRoute requiredRole="student">
              <NonChildOnly>
                <PaymentStatus />
              </NonChildOnly>
            </ProtectedRoute>
          }
        />
        <Route
          path="/Scedule-Meeting/:TeacherID"
          element={<LegacyScheduleRedirect />}
        />
        <Route path="/TeacherDetails/:TeacherID" element={<TeacherDetails />} />
        <Route path="/CourseDetails/:CourseID" element={<CourseDetails />} />
        <Route path="/direct-link/:id" element={<DirectLinkPage />} />
        <Route path="/mobile-signin" element={<MobileSignIn 
          onForgotPassword={() => {
            window.location.href = '/forgot-password';
          }}
          onGoogleSignIn={() => {
            // Handle Google sign in - you can customize this
            alert('Google sign in - implement as needed');
          }}
          onSwitchToSignup={() => {
            // Navigate to signup page or show signup modal
            window.location.href = '/new-signup';
          }}


        />} />
        {/* Teacher Invite Signup */}
        <Route path="/invite/:token" element={<InviteSignupPage />} />
        {/*---------------------------------------------------------------------------------------------- Student Dashboard Routes --------------- */}
        <Route
          path="/Student-dashboard"
          element={
            <ProtectedRoute requiredRole="student">
              <ThemeProvider>
                <StudentDashboard />
              </ThemeProvider>
            </ProtectedRoute>
          }
        >
	      <Route index element={<Navigate to="dash" replace />} />
	      <Route
            path="dash"
            element={
              <StudentDashMobileComplete />
            }
          />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="profile" element={<Profile />} />
          <Route
            path="wallet"
            element={
              <NonChildOnly>
                <WalletPage />
              </NonChildOnly>
            }
          />
          <Route
            path="payments"
            element={
              <NonChildOnly>
                <StudentPaymentHistoryPage />
              </NonChildOnly>
            }
          />
          <Route path="buy-lessons" element={<NonChildOnly><BuyLessonsPage /></NonChildOnly>} />
          <Route path="subscriptions/:planCode/schedule" element={<NonChildOnly><SubscriptionSchedulePage /></NonChildOnly>} />
          <Route path="subscriptions/:planCode/checkout" element={<NonChildOnly><SubscriptionPaymentPage /></NonChildOnly>} />
          <Route path="book-lessons" element={<BookLessonsPage />} />
          <Route
            path="referrals"
            element={
              <NonChildOnly>
                <ReferralDashboard />
              </NonChildOnly>
            }
          />
          <Route
            path="family-accounts"
            element={
              <NonChildOnly>
                <ChildZonePage />
              </NonChildOnly>
            }
          />
          <Route
            path="evaluation"
            element={<StudentEvaluationsPage />}
          />
          <Route
            path="materials"
            element={<StudentMaterials />}
          />
          <Route
            path="practice"
            element={<PracticePage />}
          />
          <Route
            path="teachers"
            element={<StudentTeacherDirectory />}
          />
          <Route
            path="history"
            element={
              <NonChildOnly>
                <StudentHistoryPage />
              </NonChildOnly>
            }
          />
          <Route path="Bookings" element={<Bookings />} />
          <Route
            path="Bookings/edit-Booking/:BookingID"
            element={<EditBooking />}
          />
          <Route
            path="Payments/Invoice/:id"
            element={
              <NonChildOnly>
                <StudentInvoice />
              </NonChildOnly>
            }
          />
          <Route path="Enquirys" element={<Enquirys />} />
          <Route
            path="Enquirys/add-enquirys/:StudentID"
            element={<AddStudentEnquiry />}
          />
        </Route>
        {/* --------------------------------------------------------------------------------------------------------------Teachers Panel Routes ------------- */}
        <Route
          path="/Teacher-dashboard"
          element={
            <ProtectedRoute requiredRole="teacher">
              <TeacherDashboard />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/Teacher-dashboard/dash" replace />} />
          <Route path="/Teacher-dashboard/dash" element={<TeacherDash />} />
          <Route path="/Teacher-dashboard/notifications" element={<NotificationsPage />} />
          <Route
            path="/Teacher-dashboard/profile"
            element={<TeacherProfile />}
          />
          <Route
            path="/Teacher-dashboard/Edit-Availability/:id"
            element={<TeacherEditAvailability />}
          />
          <Route
            path="/Teacher-dashboard/Edit-Availability-beta/:id"
            element={<TeacherEditAvailabilityBeta />}
          />
          <Route
            path="/Teacher-dashboard/Bookings"
            element={
              <TeacherThemeProvider>
                {/* Teacher bookings reuses some student dashboard UI (filters), which depends on the student ThemeContext. */}
                <ThemeProvider>
                  <TeacherBookings />
                </ThemeProvider>
              </TeacherThemeProvider>
            }
          />
          {/* <Route
            path="/Teacher-dashboard/setting"
            element={<TeacherSetting />}
          /> */}

          <Route path="/Teacher-dashboard/Wallet" element={<TeacherWallet />} />
          <Route path="/Teacher-dashboard/lesson-center" element={<TeacherLessonCenter />} />
          <Route path="/Teacher-dashboard/evaluations" element={<TeacherEvaluationsPage />} />
          <Route path="/Teacher-dashboard/student-ratings" element={<TeacherStudentRatings />} />
          <Route path="/Teacher-dashboard/materials" element={<TeacherMaterialsPage />} />
          <Route path="/Teacher-dashboard/notes" element={<TeacherNotesPage />} />
          <Route path="/Teacher-dashboard/history" element={<TeacherHistoryPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        {/* ------------------------------------------------------------------------------------------------------------- Admin Panel Routes  ---------------  */}
        {/* Admin  Dashboard Routes */}
        <Route
          path="/Admin-Dashboard"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/Admin-Dashboard/Dashboard" replace />} />
          <Route path="/Admin-Dashboard/Notifications" element={<AdminNotificationsPage />} />
          <Route path="/Admin-Dashboard/Dashboard" element={<AdminOperationsDashboardPage />} />
          <Route path="/Admin-Dashboard/Teachers" element={<AdminTeachers />} />
          <Route
            path="/Admin-Dashboard/Teachers/add-teacher"
            element={<AdminAddTeachers />}
          />
          <Route
            path="/Admin-Dashboard/Teachers/add-teacher/add-availability"
            element={<AdminAddAvailability />}
          />
          <Route
            path="/Admin-Dashboard/Teachers/add-teacher/add-availability/add-time"
            element={<AdminAddAvailabilityTime />}
          />
          <Route
            path="/Admin-Dashboard/Teachers/edit-teacher/:id"
            element={<AdminEditTeacher />}
          />
          <Route
            path="/Admin-Dashboard/Teachers/edit-teacher/add-availability/:id"
            element={<AdminEditTeacherEditAvailability />}
          />
          <Route
            path="/Admin-Dashboard/Teachers/teachersDetails/:id"
            element={<AdminTeachersDetails />}
          />
          <Route path="/Admin-Dashboard/Archive/Teachers" element={<AdminTeachers />} />
          <Route path="/Admin-Dashboard/Students" element={<AdminPeoplePage />} />
          <Route path="/Admin-Dashboard/Archive/Students" element={<AdminStudents />} />
          <Route
            path="/Admin-Dashboard/Students/add-student"
            element={<AdminAddStudents />}
          />
          <Route
            path="/Admin-Dashboard/Students/edit-student/:id"
            element={<AdminEditStudent />}
          />

          <Route path="/Admin-Dashboard/Counsellors" element={<AdminCounsellors />} />

          <Route path="/Admin-Dashboard/Bug-Reports" element={<BugList />} />

          <Route
            path="/Admin-Dashboard/xlxs-files"
            element={<ExportComponent />}
          />

          <Route
            path="/Admin-Dashboard/Evaluation"
            element={<AdminEvaluationDashboard />}
          />
          <Route
            path="/Admin-Dashboard/Referral"
            element={<AdminReferralDashboard />}
          />

          <Route
            path="/Admin-Dashboard/online-users"
            element={<AdminOnlineUsersPage currentUser={currentUser} />}
          />

          <Route
            path="/Admin-Dashboard/Students/studentsDetails/:id"
            element={<AdminStudentsDetails />}
          />
          <Route path="/Admin-Dashboard/Practice" element={<AdminPracticeMaterials />} />
          <Route path="/Admin-Dashboard/Bookings" element={<AdminBookings />} />
          <Route path="/Admin-Dashboard/Archive/Bookings" element={<AdminBookings />} />
          <Route path="/Admin-Dashboard/Teacher-Invites" element={<TeacherInvitesPage teachers={[]} />} />
          <Route
            path="/Admin-Dashboard/Bookings/add-Booking"
            element={<AdminAddBooking />}
          />
          <Route
            path="/Admin-Dashboard/Bookings/edit-booking/:BookingID"
            element={<AdminEditBooking />}
          />
          <Route
            path="/Admin-Dashboard/Settings/Admin-Settings/:email"
            element={<AdminSettings />}
          />
          <Route path="/Admin-Dashboard/Payments" element={<AdminPayments />} />
          <Route
            path="/Admin-Dashboard/Payments/Invoice/:id"
            element={<Invoice />}
          />
          <Route path="/Admin-Dashboard/Finance" element={<AdminFinance />} />
          <Route path="/Admin-Dashboard/Live-Sessions" element={<AdminLiveSessions />} />
          <Route path="/Admin-Dashboard/Session-History" element={<AdminSessionHistory />} />
          <Route path="/Admin-Dashboard/AI-Chat-Logs" element={<AdminAiChatLogs />} />
          <Route path="/Admin-Dashboard/Activity-Log" element={<AdminActivityLog />} />
          <Route path="/Admin-Dashboard/LeadCenter" element={<AdminLeadCenter />} />
          <Route path="/Admin-Dashboard/VoiceLeads" element={<AdminVoiceLeads />} />
          <Route path="/Admin-Dashboard/VoiceLeads/:id" element={<AdminVoiceLeadDetail />} />
          <Route path="/Admin-Dashboard/Wallets" element={<AdminWallets />} />
          <Route path="/Admin-Dashboard/Teacher-Notes" element={<AdminTeacherNotes />} />
          <Route path="/Admin-Dashboard/Teacher-Ratings" element={<AdminTeacherRatings />} />
          <Route path="/Admin-Dashboard/Subscriptions" element={<AdminSubscriptions />} />
          <Route path="/Admin-Dashboard/Enquiries" element={<AdminEnquirys />} />
          <Route path="/Admin-Dashboard/Settings" element={<AdminSettings />} />
        </Route>
        {/* -----------------------------------------------------------------------------------------------Accountant Routes */}
        <Route
          path="/Accountant-Dashboard"
          element={
            <ProtectedRoute requiredRole="accountant">
              <AccontantDashboard />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/Accountant-Dashboard/dash" replace />} />
          <Route path="/Accountant-Dashboard/dash" element={<AccontantDash />} />
          {/* Accountant notification route removed */}
          <Route
            path="/Accountant-Dashboard/Payments"
            element={<AccontantPayment />}
          />
          <Route
            path="/Accountant-Dashboard/Payments/Invoice/:id"
            element={<AccountantInvoice />}
          />
          <Route
            path="/Accountant-Dashboard/Package"
            element={<Navigate to="/Accountant-Dashboard/Payments" replace />}
          />
        </Route>
        {/* Counsellor Routes */}
        <Route path="/counsellor-login" element={<CounsellorLogin />} />
        <Route
          path="/Counsellor-Dashboard"
          element={
            <CounsellorProtectedRoute>
              <CounsellorDashboard />
            </CounsellorProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/Counsellor-Dashboard/Dashboard" replace />} />
          <Route path="/Counsellor-Dashboard/Dashboard" element={<CounsellorDash />} />
          <Route path="/Counsellor-Dashboard/DecisionQueue" element={<CounsellorDecisionQueuePage />} />
          <Route path="/Counsellor-Dashboard/Invites" element={<CounsellorInvitesPage />} />
          <Route path="/Counsellor-Dashboard/TrialBookings" element={<CounsellorTrialBookingsPage />} />
          <Route path="/Counsellor-Dashboard/VoiceLeads" element={<CounsellorVoiceLeadsPage />} />
          <Route path="/Counsellor-Dashboard/VoiceLeads/:id" element={<CounsellorVoiceLeadDetail />} />
          <Route path="/Counsellor-Dashboard/Teachers" element={<CounsellorTeachersPage />} />
          <Route path="/Counsellor-Dashboard/Profile" element={<CounsellorProfilePage />} />
        </Route>

        {/* BUG-015: Catch-all 404 for undefined paths */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {!hideAiChatWidget && <AiChatWidget suppressLauncher={isStudentHelpChatRoute} />}
        </>
        )}
        </Suspense>
        </ErrorBoundary>

      {/* One persistent toast provider owns the reviewed dashboard rails. */}
      <CookieConsentBanner />
      </div>
    </AppToastProvider>
    </NovuProvider>
    </HelmetProvider>
  );
};

export default App;
